import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { get } from '../../config/api';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { ROLES } from '../../constants/textconstants';

// Define the shape of a single request item
interface RequestData {
  request_id: number;
  partner_id: string;
  govt_application_number: string;
  gst_number: string;
  license_number: string;
  license_issued_date: string;
  license_expiry_date: string;
  shop_address: string;
  shop_district: string;
  shop_pincode: string;
  godown_same_as_shop: boolean;
  godown_address: string;
  godown_district: string;
  godown_pincode: string;
  assigned_to: string;
  status: string;
  remarks: string;
  created_at: string;
  updated_at: string;
  due_date: string;
  license_copy_url: any[]; // Assuming an array of objects or strings
  products: string[];
}

// Define the shape of the entire slice state
interface PendingRequestsState {
  pendingRequest: RequestData[];
  data: RequestData[];
  issuedCertificates: RequestData[];
  loading: boolean;
  error: string | null;
  loadingIssued: boolean;
  loadingDashboard: boolean;
  errorIssued: string | null;
  errorLoadingDashboard: string | null;
  dashboardSummary:any;
  tabPages: { [tab: string]: number };
  activeTab: string;
  issuedCertificatesPage: number;
}

// Initial state with an empty data array
const initialState: PendingRequestsState = {
  pendingRequest: [],
  data: [],
  issuedCertificates: [],
  loading: false,
  error: null,
  loadingIssued: false,
  loadingDashboard:false,
  errorIssued: null,
  errorLoadingDashboard:null,
  dashboardSummary: {},
  tabPages: {},
  activeTab: '',
  issuedCertificatesPage: 1,
};

export const fetchPendingRequestsData = createAsyncThunk(
  'pendingRequests/fetchData',
  async (userID: string) => {
    const params = new URLSearchParams({
        status: 'PENDING_APPROVAL',
        page:'1',
        size: '5',
      });

    try {
      const response = await get(`${API_ENDPOINTS.CERTIFICATE_REQUESTS_BY_USER(userID)}?${params.toString()}`);
      const data = response.items;
      return data;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch pending requests data');
    }
  }
);

export const fetchIssuedCertificatesData = createAsyncThunk(
  'pendingRequests/fetchIssuedData',
  async (info: any) => {

     const params = new URLSearchParams({
        status: 'ACTIVE',
        page:'1',
        size: '10',
      });

    try {
       const response = await get(`${API_ENDPOINTS.CERTIFICATE_REQUESTS_BY_USER(info.userId)}?${params.toString()}`);
        const data = response.items;
        return data;
     
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch issued certificates data');
    }
  }
);

// Create the pendingRequests slice
const pendingRequestsSlice = createSlice({
  name: 'pendingRequests',
  initialState,
  reducers: {
   
    updateCurrentRequest: (state, action: PayloadAction<number>) => {
      const requestIdToUpdate = action.payload;
      const requestToUpdate = state.data.find(
        (request) => request.request_id === requestIdToUpdate
      );

      if (requestToUpdate) {
        requestToUpdate.status = 'Approved';
      }
      state.pendingRequest = state.pendingRequest.filter(
        (request) => request.request_id !== requestIdToUpdate
      );
    },
    updateCurrentRequestActive: (state, action: PayloadAction<number>) => {
      const requestIdToUpdate = action.payload;
      state.data = state.data.map((request) =>
        request.request_id === requestIdToUpdate
          ? { ...request, status: 'Active' }
          : request
      );
    },
    setTabPage: (state, action: PayloadAction<{ tab: string; page: number }>) => {
      state.tabPages[action.payload.tab] = action.payload.page;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setIssuedCertificatesPage: (state, action: PayloadAction<number>) => {
      state.issuedCertificatesPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(REHYDRATE, (state) => {
        state.tabPages = {};
        state.activeTab = '';
        state.issuedCertificatesPage = 1;
      })
      .addCase(fetchPendingRequestsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingRequestsData.fulfilled, (state, action: PayloadAction<RequestData[]>) => {
        state.loading = false;
        state.pendingRequest = action.payload;
        state.data = action.payload;
      })
      .addCase(fetchPendingRequestsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch pending requests';
      })
      .addCase(fetchIssuedCertificatesData.pending, (state) => {
        state.loadingIssued = true;
        state.errorIssued = null;
      })
      .addCase(fetchIssuedCertificatesData.fulfilled, (state, action: PayloadAction<RequestData[]>) => {
        state.loadingIssued = false;
        state.issuedCertificates = action.payload;
      })
      .addCase(fetchIssuedCertificatesData.rejected, (state, action) => {
        state.loadingIssued = false;
        state.errorIssued = action.error.message || 'Failed to fetch issued certificates';
      })
  },
});

// Export the action creator
export const { updateCurrentRequest, updateCurrentRequestActive, setTabPage, setActiveTab, setIssuedCertificatesPage } = pendingRequestsSlice.actions;

export default pendingRequestsSlice.reducer;
